import {DecimalPipe} from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {deserializeAsset, VideoAsset} from '@app/asset';
import {InspectorService} from '@app/inspector/inspector.service';
import {Matrix} from '@app/shared/euclid';
import {ManagedComponent} from '@app/shared/managed.component';
import {VideoProcessingComponent} from '@app/shared/video-processing/video-processing.component';
import {ViewerToolsService} from '@app/viewer/viewer-tools.service';
import {fromEvent} from 'rxjs';
import videojs, {VideoJsPlayerOptions} from 'video.js';
import {IViewable, ViewerTool} from '../viewable.interface';

@Component({
    selector: 'frog-video',
    templateUrl: './video.component.html',
    styleUrls: ['./video.component.scss'],
    imports: [DecimalPipe, VideoProcessingComponent],
})
export class VideoComponent
    extends ManagedComponent
    implements OnInit, OnDestroy, AfterViewInit, IViewable, OnChanges
{
    @Input() asset: VideoAsset;
    @Input() embedded: boolean;
    @ViewChild('video') video: ElementRef;
    @ViewChild('root', { static: true }) root: ElementRef;

    public xform: Matrix;
    public margin: number;
    public width: number;
    public height: number;
    public frame: number;
    public frameCount: number;
    public player: videojs.Player;
    private infoPadding: number = 64;

    constructor(
        private inspectorService: InspectorService,
        private viewerToolsService: ViewerToolsService,
    ) {
        super();

        this.xform = Matrix.Identity();
    }

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.asset) {
            const currentVideo = deserializeAsset(changes.asset.currentValue) as VideoAsset;
            const previousVideo = deserializeAsset(changes.asset.previousValue);

            if (this.player !== undefined && !currentVideo.equals(previousVideo)) {
                this.player.src({ type: 'video/mp4', src: currentVideo.video });
                setTimeout(() => {
                    this.xform = Matrix.Identity();
                    this.xform.elements[0][0] = this.asset.width;
                    this.xform.elements[1][1] = this.asset.height;
                    this.scaleToWindow();

                    this.play();
                }, 0);
            }
        }
    }

    ngAfterViewInit() {
        if (this.embedded) {
            this.infoPadding = 0;
        }

        if (!this.video) {
            return;
        }

        const element = this.video.nativeElement;
        let sub = fromEvent(element, 'timeupdate').subscribe((event) => {
            this.frame = Math.floor(this.asset.framerate * element.currentTime);
        });
        this.subscriptions.push(sub);
        sub = fromEvent(this.video.nativeElement, 'canplay').subscribe((event) => {
            this.frameCount = Math.round(this.asset.framerate * element.duration);
        });
        this.subscriptions.push(sub);

        fromEvent(this.video.nativeElement, 'loadeddata').subscribe(() => {
            this.xform = Matrix.Identity();
            this.xform.elements[0][0] = this.asset.width;
            this.xform.elements[1][1] = this.asset.height;
            this.scaleToWindow();

            this.play();
        });

        element.load();

        const playerOptions: VideoJsPlayerOptions = {
            fluid: true,
            controlBar: {
                pictureInPictureToggle: false,
            },
        };
        this.player = videojs(this.video.nativeElement, playerOptions);
        this.player.ready(() => {
            // The player is ready so let the viewer know about tools available
            const viewerTools = [
                new ViewerTool({
                    icon: 'fullscreen',
                    tooltip: 'Fullscreen',
                    action: () => {
                        this.player.requestFullscreen();
                    },
                }),
            ];

            this.viewerToolsService.setTools(viewerTools);
        });
    }

    private play(): void {
        this.player.play()
            .catch(error => {
                // Ignore interruption errors
                // https://developer.chrome.com/blog/play-request-was-interrupted
            });
    }

    ngOnDestroy() {
        super.ngOnDestroy();

        if (this.player) {
            this.player.dispose();
        }
    }

    @HostListener('window:keydown', ['$event'])
    keyDownEvent(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            // User is editing a form control
            return;
        }

        if (this.inspectorService.isInspecting) {
            return;
        }

        let triggered = false;
        let multiplier = 1;
        const frames = this.asset.framerate * this.asset.duration;
        const step = this.asset.duration / frames;
        const currentTime = this.player.currentTime();

        if (event.key === ',') {
            triggered = true;
            multiplier = -1;
        }
        if (event.key === 'j') {
            triggered = true;
            multiplier = -5;
        }
        if (event.key === '.') {
            triggered = true;
            multiplier = 1;
        }
        if (event.key === 'l') {
            triggered = true;
            multiplier = 5;
        }

        if (event.key === 'k' || event.code === 'Space') {
            this.player.paused() ? this.play() : this.player.pause();
        }

        if (triggered) {
            event.stopPropagation();
            event.preventDefault();

            this.player.currentTime(currentTime + step * multiplier);
            this.player.pause();
        }
    }

    center(scale: number = 1.0) {
        this.xform = new Matrix([
            [this.asset.width, 0, 0],
            [0, this.asset.height, 0],
            [0, 0, 1],
        ]);
        this.scale(scale, scale);
        const x: number = this.asset.width / 2 - this.xform.elements[0][0] / 2;
        const y: number = this.asset.height / 2 - this.xform.elements[1][1] / 2;
        this.translate(x, y);
    }

    scaleToSource() {
        this.center();
        this.margin = window.innerHeight / 2 - (this.xform.elements[1][1] + this.infoPadding) / 2;
    }

    scaleToWindow() {
        const size = this.xform.rect.fit(
            this.root.nativeElement.clientWidth,
            this.root.nativeElement.clientHeight - (this.infoPadding * 2),
        );
        const scale = size.width / this.asset.width;
        this.center(scale);

        this.margin = this.infoPadding;
    }

    translate(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [1, 0, 0],
            [0, 1, 0],
            [x, y, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();
    }

    scale(x: number, y: number) {
        const m1: Matrix = new Matrix([
            [x, 0, 0],
            [0, y, 0],
            [0, 0, 1],
        ]);
        const m2: Matrix = this.xform.x(m1);
        this.xform = m2.dup();
    }
}
